/** @jsxImportSource @emotion/react */
import React from "react";
import { Button, Input, message, Space, Switch, Table } from "antd";
import {
  LayoutContent,
  TypographySmall,
  LayoutContentFilterBar,
  SEO,
} from "../../../components";
import { FilterItem } from "../../../components/LayoutContentFilterBar";
import {
  TenantUserUsecase,
  useTenantUserDispatch,
  useTenantUserState,
} from "../../../usecases/TenantUser";
import {
  FilterParams,
  ModalTenantUserFilter,
} from "../../../components/TenantUser/ModalTenantUserFilter";
import * as Styles from "./styles";
import lodash from "lodash";
import { Images } from "../../../common/images";
import { ModalAddTenantUser } from "../../../components/TenantUser/ModalAddTenantUser";
import { ModalDeleteTenantUser } from "../../../components/TenantUser/ModalDeleteTenantUser";
import { GetTenantUserDto } from "../../../services/TenantUser/dto/get.dto";
import { ModalEditTenantUser } from "../../../components/TenantUser/ModalEditTenantUser";
import { useAuthState } from "../../../usecases/Auth";

const TenantUserComponent: React.FC = () => {
  const authState = useAuthState();

  const TenantUserState = useTenantUserState();
  const TenantUserDispatch = useTenantUserDispatch();

  const [openNewTenantUserModal, setOpenNewTenantUserModal] =
    React.useState(false);
  const [openEditTenantUserModal, setOpenEditTenantUserModal] =
    React.useState(false);
  const [openDeleteTenantUserModal, setOpenDeleteTenantUserModal] =
    React.useState(false);
  const [openFilterModal, setOpenFilterModal] = React.useState(false);
  const [selectedTenantUser, setSelectedTenantUser] =
    React.useState<GetTenantUserDto>();
  const [filter, setFilter] = React.useState<FilterParams>({});

  const filterArrayMemo = React.useMemo(() => {
    const filterArray: FilterItem[] = [];
    const filterObject = filter as Record<string, string>;
    for (const key in filter) {
      if (key === "keyword" || !filterObject[key]) {
        continue;
      }
      let label = key.replace(/([A-Z])/g, " $1");
      label = label.charAt(0).toUpperCase() + label.slice(1);

      filterArray.push({
        label,
        key,
        value: filterObject[key],
      });
    }
    return filterArray;
  }, [filter]);

  const onItemStatusChangeHandle = React.useCallback(
    (value) => async (checked: boolean) => {
      if (TenantUserDispatch) {
        const tenantUserUsecase = new TenantUserUsecase(TenantUserDispatch);
        await tenantUserUsecase.updateStatus({
          id: value.id,
          isActive: checked,
        });
      }
    },
    [TenantUserDispatch],
  );

  const onButtonAddNewClickHandle = React.useCallback(() => {
    setOpenNewTenantUserModal(true);
  }, []);

  const onModalAddTenantUserCancelHandle = React.useCallback(() => {
    setOpenNewTenantUserModal(false);
  }, []);

  const onModalAddTenantUserSuccessHandle = React.useCallback(() => {
    setOpenNewTenantUserModal(false);
    message.success("Created new System User successful");
  }, []);

  const onItemEditClickHandle = React.useCallback(
    (value) => () => {
      setSelectedTenantUser(value);
      setOpenEditTenantUserModal(true);
    },
    [],
  );

  const onModalEditTenantUserCancelHandle = React.useCallback(() => {
    setOpenEditTenantUserModal(false);
    setSelectedTenantUser(undefined);
  }, []);

  const onModalEditTenantUserSuccessHandle = React.useCallback(() => {
    setOpenEditTenantUserModal(false);
    setSelectedTenantUser(undefined);
    message.success("Updated system user successful");
  }, []);

  const onModalDeleteTenantUserCancelHandle = React.useCallback(() => {
    setSelectedTenantUser(undefined);
    setOpenDeleteTenantUserModal(false);
  }, []);

  const onModalDeleteTenantUserSuccessHandle = React.useCallback(() => {
    setSelectedTenantUser(undefined);
    setOpenDeleteTenantUserModal(false);
    message.success("Deleted System User successful");
  }, []);

  const onButtonDeleteClickHandle = React.useCallback(
    (item) => () => {
      setSelectedTenantUser(item);
      setOpenDeleteTenantUserModal(true);
    },
    [],
  );

  const onFilterClickHandle = React.useCallback(() => {
    setOpenFilterModal(true);
  }, []);

  const onFilterItemCloseHandle = React.useCallback(
    (item: FilterItem) => {
      const filterClone = {
        ...filter,
      } as Record<string, string | undefined>;
      for (const key in filter) {
        if (key === item.key) {
          delete filterClone[key];
          break;
        }
      }
      setFilter(filterClone as FilterParams);
    },
    [filter],
  );

  const onModalTenantUserFilterCancelHandle = React.useCallback(() => {
    setOpenFilterModal(false);
  }, []);

  const onModalTenantUserFilterSearchHandle = React.useCallback(
    (newFilter: FilterParams) => {
      setFilter(newFilter);
      setOpenFilterModal(false);
    },
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onKeywordChangeHandle = React.useCallback(
    lodash.debounce((event) => {
      setFilter({
        ...filter,
        keyword: event.target.value,
      });
    }, 300),
    [],
  );

  React.useEffect(() => {
    async function asyncLoadData() {
      if (TenantUserDispatch) {
        const tenantUserUsecase = new TenantUserUsecase(TenantUserDispatch);
        await tenantUserUsecase.getList(filter);
      }
    }

    asyncLoadData();
  }, [TenantUserDispatch, filter]);

  return (
    <LayoutContent
      title="System Users"
      toolbar={
        <Space size="large">
          <Input.Search
            placeholder="Search..."
            allowClear
            style={{ width: 300, verticalAlign: "middle" }}
            size="large"
            onChange={onKeywordChangeHandle}
          />
          <Button
            type="primary"
            size="large"
            onClick={onButtonAddNewClickHandle}
          >
            <img src={Images.ADD} css={Styles.iconStyle} />
            <span>Add new</span>
          </Button>
        </Space>
      }
    >
      <SEO title="Home Builder System Users" />
      <ModalAddTenantUser
        visible={openNewTenantUserModal}
        onCancel={onModalAddTenantUserCancelHandle}
        onSuccess={onModalAddTenantUserSuccessHandle}
      />
      {selectedTenantUser && openEditTenantUserModal && (
        <ModalEditTenantUser
          visible={openEditTenantUserModal}
          onCancel={onModalEditTenantUserCancelHandle}
          onSuccess={onModalEditTenantUserSuccessHandle}
          item={selectedTenantUser}
        />
      )}
      {selectedTenantUser && openDeleteTenantUserModal && (
        <ModalDeleteTenantUser
          visible={openDeleteTenantUserModal}
          onCancel={onModalDeleteTenantUserCancelHandle}
          onSuccess={onModalDeleteTenantUserSuccessHandle}
          item={selectedTenantUser}
        />
      )}
      <ModalTenantUserFilter
        visible={openFilterModal}
        onCancel={onModalTenantUserFilterCancelHandle}
        onSearch={onModalTenantUserFilterSearchHandle}
        filter={filter}
      />
      <LayoutContentFilterBar
        onFilterClick={onFilterClickHandle}
        onFilterClose={onFilterItemCloseHandle}
        filters={filterArrayMemo}
      />
      <Table
        dataSource={TenantUserState.data}
        css={Styles.tableStyle}
        pagination={false}
      >
        <Table.Column
          title="Full Name"
          align="center"
          render={(value) => (
            <TypographySmall>{`${value.userFirstName} ${value.userLastName}`}</TypographySmall>
          )}
        />
        <Table.Column
          title="User Role"
          dataIndex="userRole"
          key="userRole"
          align="center"
        />
        <Table.Column
          title="Email"
          dataIndex="userEmail"
          key="userEmail"
          align="center"
        />
        <Table.Column
          title="Status"
          render={(value) =>
            value.status === "Activated" ? (
              value.id === authState.userInfo?.username ? (
                <></>
              ) : (
                <Switch
                  onChange={onItemStatusChangeHandle(value)}
                  checked={value.isActive}
                />
              )
            ) : (
              <img src={Images.WAITING_STA} css={Styles.waitingIconStyle} />
            )
          }
        />
        <Table.Column
          render={(value) =>
            value.status === "Activated" ? (
              <Button type="link" onClick={onItemEditClickHandle(value)}>
                <TypographySmall>Edit</TypographySmall>
              </Button>
            ) : (
              <Button type="link" onClick={onButtonDeleteClickHandle(value)}>
                <TypographySmall type="danger">Delete</TypographySmall>
              </Button>
            )
          }
        />
      </Table>
    </LayoutContent>
  );
};

export const TenantUser = React.memo(TenantUserComponent);
